/**
 * Modal Component
 * 
 * A reusable modal dialog component with customizable content, header, and actions.
 * Implements accessibility features and keyboard navigation.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/tailwind-utils'
import Button from './button'

interface ModalProps {
  /**
   * Whether the modal is currently open
   */
  isOpen: boolean
  
  /**
   * Function to call when the modal should close
   */
  onClose: () => void
  
  /**
   * Title to display in the modal header
   */
  title?: string
  
  /**
   * Content to display in the modal body
   */
  children: React.ReactNode
  
  /**
   * Additional actions to display in the modal footer
   */
  actions?: React.ReactNode
  
  /**
   * Whether to show a close button in the header
   * @default true
   */
  showCloseButton?: boolean
  
  /**
   * Additional CSS classes to apply to the modal
   */
  className?: string
  
  /**
   * Size variant of the modal
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  
  /**
   * Whether to close the modal when clicking outside
   * @default true
   */
  closeOnOutsideClick?: boolean
  
  /**
   * Whether to close the modal when pressing Escape
   * @default true
   */
  closeOnEsc?: boolean
}

/**
 * Modal component for displaying content in a dialog overlay
 * 
 * @example
 * // Basic usage
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Example Modal">
 *   <p>Modal content goes here</p>
 * </Modal>
 * 
 * @example
 * // With custom actions
 * <Modal 
 *   isOpen={isOpen} 
 *   onClose={() => setIsOpen(false)} 
 *   title="Confirm Action"
 *   actions={
 *     <>
 *       <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to continue?</p>
 * </Modal>
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  actions,
  showCloseButton = true,
  className,
  size = 'md',
  closeOnOutsideClick = true,
  closeOnEsc = true,
}: ModalProps) {
  const [isMounted, setIsMounted] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  
  // Handle mounting state for SSR compatibility
  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])
  
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return
    
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, closeOnEsc])
  
  // Handle click outside
  function handleOutsideClick(event: React.MouseEvent) {
    if (closeOnOutsideClick && modalRef.current && !modalRef.current.contains(event.target as Node)) {
      onClose()
    }
  }
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])
  
  // Size classes mapping
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  }
  
  if (!isMounted || !isOpen) return null
  
  return createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      onClick={handleOutsideClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div 
        ref={modalRef}
        className={cn(
          'bg-card border border-border shadow-lg rounded-lg w-full',
          sizeClasses[size],
          'flex flex-col max-h-[calc(100vh-2rem)] animate-in fade-in-0 zoom-in-95',
          className
        )}
      >
        {/* Modal header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-4 border-b border-border">
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label="Close modal"
                className="p-1 h-auto"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </Button>
            )}
          </div>
        )}
        
        {/* Modal body */}
        <div className="p-4 overflow-y-auto">{children}</div>
        
        {/* Modal footer */}
        {actions && (
          <div className="flex justify-end gap-2 p-4 border-t border-border">
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default Modal
